import { useState, useCallback } from 'react';
import { descartarSessionId } from '@/shared/lib/jukeboxSession';
import { enviarOtp, verificarOtp } from '../../data/jukeboxClienteService';
import type { JukeboxCliente } from '../contexts/JukeboxClienteContext';

export type OtpStep = 'phone' | 'code' | 'success' | 'error';

interface UseJukeboxAuthParams {
  telaId: string;
  sessionId: string;
  onSuccess: (cliente: JukeboxCliente) => void;
}

interface UseJukeboxAuthReturn {
  step: OtpStep;
  loading: boolean;
  error: string | null;
  sendOtp: (phoneE164: string) => Promise<void>;
  verifyOtp: (phoneE164: string, codigo: string) => Promise<void>;
  reset: () => void;
}

export function useJukeboxAuth({ telaId, sessionId, onSuccess }: UseJukeboxAuthParams): UseJukeboxAuthReturn {
  const [step, setStep] = useState<OtpStep>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendOtp = useCallback(async (phoneE164: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await enviarOtp(telaId, sessionId, phoneE164);

      if (fnError) throw new Error(fnError.message);
      if (!data?.ok) {
        const msg = data?.erro === 'limite_atingido'
          ? 'Muitas tentativas. Aguarde 10 minutos.'
          : 'Erro ao enviar código. Tente novamente.';
        setError(msg);
        return;
      }

      setStep('code');
    } catch {
      setError('Erro de conexão. Verifique sua internet.');
    } finally {
      setLoading(false);
    }
  }, [telaId, sessionId]);

  const verifyOtp = useCallback(async (phoneE164: string, codigo: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await verificarOtp(telaId, sessionId, phoneE164, codigo);

      if (fnError) throw new Error(fnError.message);
      if (data?.erro === 'sessao_em_uso') {
        descartarSessionId(telaId);
        window.location.reload();
        return;
      }
      if (!data?.ok) {
        setError('Código incorreto ou expirado.');
        return;
      }

      setStep('success');
      onSuccess(data.cliente);
    } catch {
      setError('Erro de conexão. Verifique sua internet.');
    } finally {
      setLoading(false);
    }
  }, [telaId, sessionId, onSuccess]);

  const reset = useCallback(() => {
    setStep('phone');
    setError(null);
    setLoading(false);
  }, []);

  return { step, loading, error, sendOtp, verifyOtp, reset };
}
